import { useState, useRef, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import StepNav, { DRUGSTORAGE_STEPS, DRUGSTORAGE_STEPS_CHALLENGE } from "@/components/StepNav";
import {
  PhaseStepper, ChallengeCard, IdentifyPanel, StorePanel, RegisterPanel, InspectPanel, StorageDebrief,
} from "@/components/drugstorage/StoragePanels";
import { Button } from "@/components/ui/button";
import {
  SCHEDULE_ZONE, REGISTER_SCENARIO, buildInspection, INSPECT_SECONDS, pickDrugs,
} from "@/data/scheduledDrugs";
import { ShieldCheck, ChevronLeft, RotateCcw, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const SCHED_LABEL = { unscheduled: "Unscheduled", S3: "S3", S4: "S4", S8: "S8" };
const ZONE_LABEL = { general: "open shelf", cabinet: "locked S4 cabinet", safe: "S8 safe" };

export default function DrugStorage() {
  const navigate = useNavigate();
  const [drugs, setDrugs] = useState(() => pickDrugs(6));
  const byId = useMemo(() => Object.fromEntries(drugs.map((d) => [d.id, d])), [drugs]);
  const scenario = REGISTER_SCENARIO;

  const [phase, setPhase] = useState("identify");
  const [challenge, setChallenge] = useState(false);

  // phase 1
  const [answers, setAnswers] = useState({});
  // phase 2
  const [placements, setPlacements] = useState({});
  const [selectedId, setSelectedId] = useState(null);
  // phase 3
  const [closingInput, setClosingInput] = useState("");
  const [closingConfirmed, setClosingConfirmed] = useState(false);
  const [closingCorrect, setClosingCorrect] = useState(false);
  const [actionChoice, setActionChoice] = useState(null);
  const [safeguards, setSafeguards] = useState([]);
  const [safeguardsLocked, setSafeguardsLocked] = useState(false);
  // phase 4 (challenge) · inspection
  const [stations, setStations] = useState([]);
  const [found, setFound] = useState([]);
  const [inspectRevealed, setInspectRevealed] = useState(false);
  const [timeLeft, setTimeLeft] = useState(INSPECT_SECONDS);

  const [mistakes, setMistakes] = useState([]);
  const [result, setResult] = useState(null);
  const mistakeRef = useRef([]);

  // countdown while inspecting
  useEffect(() => {
    if (phase !== "inspect" || inspectRevealed) return;
    if (timeLeft <= 0) { finishInspection(); return; }
    const t = setTimeout(() => setTimeLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, timeLeft, inspectRevealed]);

  const fail = (p, msg) => {
    mistakeRef.current = [...mistakeRef.current, { phase: p, msg }];
    setMistakes(mistakeRef.current);
    toast.error(msg);
  };

  /* ── phase 1 · identify ── */
  const doneCount = Object.keys(answers).length;
  const onAnswer = (drug, optId) => {
    if (answers[drug.id]) return;
    setAnswers((a) => ({ ...a, [drug.id]: optId }));
    if (optId === drug.schedule) toast.success(`${drug.name} — ${SCHED_LABEL[drug.schedule]} ✓`);
    else fail("identify", `${drug.name} is ${SCHED_LABEL[drug.schedule]}, not ${SCHED_LABEL[optId]}`);
  };

  /* ── phase 2 · store ── */
  const placedCount = Object.keys(placements).length;
  const onSelect = (id) => setSelectedId((s) => (s === id ? null : id));
  const onPlace = (zoneId) => {
    if (!selectedId) return;
    const drug = byId[selectedId];
    const correctZone = SCHEDULE_ZONE[drug.schedule];
    if (zoneId === correctZone) {
      setPlacements((p) => ({ ...p, [drug.id]: zoneId }));
      toast.success(`${drug.name} → ${ZONE_LABEL[zoneId]} ✓`);
    } else {
      fail("store", `${drug.name} (${SCHED_LABEL[drug.schedule]}) doesn't belong in the ${ZONE_LABEL[zoneId]} — it goes in the ${ZONE_LABEL[correctZone]}`);
    }
    setSelectedId(null);
  };

  /* ── phase 3 · register ── */
  const onConfirmClosing = () => {
    const val = Number(closingInput);
    const ok = val === scenario.expectedClosing;
    setClosingConfirmed(true);
    setClosingCorrect(ok);
    if (ok) toast.success("Closing balance correct");
    else fail("register", `Closing balance should be ${scenario.expectedClosing} ${scenario.unit} (opening − total out)`);
  };
  const onAction = (id) => {
    if (actionChoice != null) return;
    setActionChoice(id);
    const act = scenario.actions.find((a) => a.id === id);
    if (act?.correct) toast.success("Correct — discrepancies must be reported and investigated");
    else fail("register", "An S8 discrepancy must be reported & investigated — never silently adjusted or ignored");
  };
  const toggleSafeguard = (id) => {
    if (safeguardsLocked) return;
    setSafeguards((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  };
  const canFinish = closingConfirmed && actionChoice != null;

  /* ── weighted scoring (challenge adds a 4th phase & reweights) ── */
  const W = challenge
    ? { id: 30, store: 25, reg: 20, insp: 25 }
    : { id: 40, store: 35, reg: 25, insp: 0 };

  const computeResult = () => {
    const N = drugs.length;
    const correctIds = drugs.filter((d) => answers[d.id] === d.schedule).length;
    const identifyScore = Math.round((W.id * correctIds) / N);

    const storeMistakes = mistakeRef.current.filter((m) => m.phase === "store").length;
    const storeScore = Math.max(0, W.store - storeMistakes * 5);

    const closeMax = Math.round(W.reg * 0.4);
    const actMax = Math.round(W.reg * 0.32);
    const safeMax = W.reg - closeMax - actMax;
    const closingPts = closingCorrect ? closeMax : 0;
    const actionPts = scenario.actions.find((a) => a.id === actionChoice)?.correct ? actMax : 0;
    const sgTotal = scenario.safeguards.length;
    const sgRight = scenario.safeguards.filter((s) => (s.correct ? safeguards.includes(s.id) : !safeguards.includes(s.id))).length;
    const safePts = Math.round((safeMax * sgRight) / sgTotal);
    const registerScore = closingPts + actionPts + safePts;

    const phases = [
      { key: "identify", label: "Identify schedule", score: identifyScore, max: W.id },
      { key: "store", label: "Store correctly", score: storeScore, max: W.store },
      { key: "register", label: "S8 register & compliance", score: registerScore, max: W.reg },
    ];

    if (challenge) {
      const breaches = stations.filter((s) => s.breach);
      const totalBreaches = breaches.length || 1;
      const hits = breaches.filter((s) => found.includes(s.id)).length;
      const falsePos = stations.filter((s) => !s.breach && found.includes(s.id)).length;
      const inspectScore = Math.max(0, Math.min(W.insp, Math.round((W.insp * (hits - falsePos)) / totalBreaches)));
      phases.push({ key: "inspect", label: "Spot the breach", score: inspectScore, max: W.insp });
      breaches.filter((s) => !found.includes(s.id)).forEach((s) => mistakeRef.current.push({ phase: "inspect", msg: `Missed: ${s.label} — ${s.breachDetail}` }));
      stations.filter((s) => !s.breach && found.includes(s.id)).forEach((s) => mistakeRef.current.push({ phase: "inspect", msg: `False alarm: ${s.label} was compliant` }));
    }

    const total = phases.reduce((sum, p) => sum + p.score, 0);
    const grade = total >= 90 ? "A" : total >= 80 ? "B" : total >= 70 ? "C" : total >= 60 ? "D" : "F";
    return { total, grade, mistakes: [...mistakeRef.current], phases };
  };

  const onRegisterFinish = () => {
    setSafeguardsLocked(true);
    if (challenge) {
      setStations(buildInspection());
      setFound([]);
      setInspectRevealed(false);
      setTimeLeft(INSPECT_SECONDS);
      setPhase("inspect");
      toast.message("Inspection — spot every breach before time runs out!");
    } else {
      setResult(computeResult());
      setPhase("done");
    }
  };

  const toggleStation = (id) => {
    if (inspectRevealed) return;
    setFound((f) => (f.includes(id) ? f.filter((x) => x !== id) : [...f, id]));
  };

  function finishInspection() {
    if (inspectRevealed) return;
    setInspectRevealed(true);
    setResult(computeResult());
  }

  const resetRun = () => {
    mistakeRef.current = [];
    setDrugs(pickDrugs(6));
    setPhase("identify");
    setAnswers({}); setPlacements({}); setSelectedId(null);
    setClosingInput(""); setClosingConfirmed(false); setClosingCorrect(false);
    setActionChoice(null); setSafeguards([]); setSafeguardsLocked(false);
    setStations([]); setFound([]); setInspectRevealed(false); setTimeLeft(INSPECT_SECONDS);
    setMistakes([]); setResult(null);
  };
  const toggleChallenge = (val) => { setChallenge(val); resetRun(); };

  const registerVm = {
    closingInput, setClosingInput, closingConfirmed, closingCorrect, onConfirmClosing,
    actionChoice, onAction, safeguards, toggleSafeguard, safeguardsLocked, canFinish, onFinish: onRegisterFinish,
  };

  const steps = challenge ? DRUGSTORAGE_STEPS_CHALLENGE : DRUGSTORAGE_STEPS;
  const phaseStep = phase === "identify" ? 1 : phase === "store" ? 2 : phase === "register" ? 3 : phase === "inspect" ? 4 : steps.length;
  const totalBreaches = stations.filter((s) => s.breach).length;

  return (
    <div className="min-h-screen bg-[#F4F7F9]">
      <StepNav
        current={phaseStep}
        steps={steps}
        title="Scheduled Drug Storage & Compliance"
        subtitle="Identify · store · controlled-drug register"
      />
      <main className="max-w-5xl mx-auto px-6 py-8 fade-up">
        <header className="mb-6">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#0047AB] font-semibold mb-2">
            <ShieldCheck className="w-3.5 h-3.5" />
            <span>Medicines handling</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-slate-900">
            Scheduled Drug Storage &amp; Compliance
          </h1>
          <p className="text-base text-slate-600 mt-2 max-w-2xl">
            Identify each medicine's schedule under the <span className="font-semibold text-slate-900">Poisons Standard (SUSMP)</span>,
            store it in the correct secured location, then keep an accurate <span className="font-semibold text-slate-900">S8 controlled-drug register</span>.
            Phase-scored against Cert IV competencies.
          </p>
          <p className="text-[11px] text-slate-400 mt-2 max-w-2xl">
            Australian national scheduling, kept state-generic. Specific storage construction and recording rules vary by state/territory — always follow your local Drugs &amp; Poisons regulations and clinic protocols.
          </p>
        </header>

        <div className="mb-6">
          <PhaseStepper phase={phase} challenge={challenge} />
        </div>

        {phase === "done" ? (
          <div className="space-y-5">
            <StorageDebrief result={result} onReset={resetRun} />
            <Button variant="outline" onClick={() => navigate("/sections")} data-testid="back-to-home-btn" className="text-slate-600">
              <ChevronLeft className="w-4 h-4 mr-1" /> Back to Simulator Hub
            </Button>
          </div>
        ) : (
          <div className="space-y-5">
            {phase === "identify" && (
              <>
                <ChallengeCard enabled={challenge} onToggle={toggleChallenge} />
                <IdentifyPanel drugs={drugs} answers={answers} onAnswer={onAnswer} doneCount={doneCount}
                  onProceed={() => { setPhase("store"); toast.message("Now store each medicine in the right place"); }} />
              </>
            )}
            {phase === "store" && (
              <StorePanel drugs={drugs} placements={placements} selectedId={selectedId} onSelect={onSelect}
                onPlace={onPlace} placedCount={placedCount}
                onProceed={() => { setPhase("register"); toast.message("Now reconcile the S8 register"); }} />
            )}
            {phase === "register" && <RegisterPanel scenario={scenario} vm={registerVm} />}
            {phase === "inspect" && (
              <>
                <InspectPanel
                  stations={stations} found={found} revealed={inspectRevealed} timeLeft={timeLeft}
                  totalBreaches={totalBreaches} onToggle={toggleStation} onFinish={finishInspection}
                />
                {inspectRevealed && (
                  <Button onClick={() => setPhase("done")} data-testid="inspect-continue-btn"
                    className="w-full bg-emerald-600 hover:bg-emerald-500 text-white">
                    Continue to results <ArrowRight className="w-4 h-4 ml-1.5" />
                  </Button>
                )}
              </>
            )}

            {mistakes.length > 0 && phase !== "inspect" && (
              <div className="text-[11px] text-slate-500" data-testid="live-mistakes">
                {mistakes.length} note{mistakes.length === 1 ? "" : "s"} logged this run
              </div>
            )}

            <div className="flex gap-2">
              <Button onClick={resetRun} data-testid="storage-reset-btn-top" className="bg-[#0047AB] hover:bg-[#003380] text-white">
                <RotateCcw className="w-4 h-4 mr-1.5" /> Reset
              </Button>
              <Button variant="outline" onClick={() => navigate("/sections")} data-testid="back-to-home-btn" className="text-slate-600">
                <ChevronLeft className="w-4 h-4 mr-1" /> Back to Simulator Hub
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
